import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TertiaryButton, SimplePopover, palette, EmojiPicker } from '@dev/ee-components';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty, map } from 'lodash-es';
import styled from 'styled-components';

import { CampaignsStaticMergeFields } from '@/services/dictionaries/CampaignsStaticMergeFields';
import { GetContactFieldsStart, getCustomFieldsStore } from '@/store/customFields/actions';
import { ModifyEmailTemplate } from '@/store/paidNewsletterCreator/actions';

const ButtonsWrapper = styled.span`
    display: flex;
    align-items: center;
    position: absolute;
    top: 6px;
    z-index: 1;
    right: 12px;

    svg {
        fill: ${palette.gray[7]};
    }
`;

const EmojiWrapper = styled.span`
    margin-top: 7px;
    cursor: pointer;
`;

interface CustomizeSubjectProps {
    subject: string;
}

export const CustomizeSubject = ({ subject }: CustomizeSubjectProps): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [showEmoji, setShowEmoji] = useState(false);
    const anchorElRef = useRef<HTMLElement>(null);
    
    const handleSubjectChange = (subject: string): void => {
        dispatch(ModifyEmailTemplate({subject}));
    };

    useEffect(() => {
        dispatch(GetContactFieldsStart({ isUrlUpdateDisabled: true }));
    }, []);

    const { collection } = useSelector(getCustomFieldsStore);

    const getMergeFieldsData = () => {
        const mergeTags = {
            [t('general')]: map(CampaignsStaticMergeFields, (value, field) => ({
                label: value,
                value: field,
            })),
        };

        if (!isEmpty(collection)) {
            mergeTags[t('custom')] = map(collection, (el) => ({
                label: el.Name,
                value: `{${el.Name}}`,
            }));
        }

        return mergeTags;
    };

    const handleAddMergeTag = (mergeTag: string): void => {
        const newSubject = `${subject}${mergeTag}`;
        handleSubjectChange(newSubject);
    };

    const handleAddEmoji = (emoji): void => {
        const newSubject = `${subject}${emoji.native}`;
        handleSubjectChange(newSubject);
    };

    const handleEmojiClick = (event: React.MouseEvent<HTMLElement>): void => {
        anchorElRef.current = !showEmoji ? event.currentTarget : null;
        setShowEmoji(!showEmoji);
    };

    const onPopupClose = (): void => {
        setShowEmoji(false);
    };

    return (
        <ButtonsWrapper>
            <SimplePopover
                listItemWidth={400}
                type="custom"
                onItemClick={(e, obj): void => handleAddMergeTag(obj.value as string)}
                options={getMergeFieldsData()}
                listItemMaxLines={2}
                maxHeight={400}
                placement="bottom"
            >
                <TertiaryButton text={t('personalize')} style={{ marginRight: 10 }} />
            </SimplePopover>

            <EmojiWrapper onClick={handleEmojiClick}>
                <EmojiPicker
                    id="emoji-campaign-subject"
                    anchorEl={anchorElRef}
                    buttonColor={showEmoji ? palette.indigo[6] : null}
                    isOpen={showEmoji}
                    onEmojiSelect={handleAddEmoji}
                    onPopupClose={onPopupClose}
                    autoFocus
                    autoFlipPopup
                />
            </EmojiWrapper>
        </ButtonsWrapper>
    );
};
